import { format, parse, startOfWeek, endOfWeek, addWeeks, subWeeks, isWithinInterval, parseISO, formatISO, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

// Formatage des dates
export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr, { locale: fr });
};

export const formatDateForInput = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'yyyy-MM-dd');
};

export const formatDateForAPI = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatISO(dateObj, { representation: 'date' });
};

// Gestion des semaines
export const getWeekStart = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return startOfWeek(dateObj, { weekStartsOn: 1 }); // Lundi comme premier jour
};

export const getWeekEnd = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return endOfWeek(dateObj, { weekStartsOn: 1 }); // Dimanche comme dernier jour
};

export const getWeekLabel = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const weekStart = getWeekStart(dateObj);
  const weekEnd = getWeekEnd(dateObj);
  
  return `${format(weekStart, 'dd/MM', { locale: fr })} - ${format(weekEnd, 'dd/MM/yyyy', { locale: fr })}`;
};

export const getCurrentWeek = () => {
  return dateToWeekInput(new Date());
};

export const getNextWeek = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return addWeeks(dateObj, 1);
};

export const getPreviousWeek = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return subWeeks(dateObj, 1);
};

// Génération de plages de semaines
export const generateWeekRange = (startDate, endDate) => {
  const weeks = [];
  const start = getWeekStart(startDate);
  const end = getWeekStart(endDate);
  
  let currentWeek = start;
  while (currentWeek <= end) {
    weeks.push(new Date(currentWeek));
    currentWeek = addWeeks(currentWeek, 1);
  }
  
  return weeks;
};

export const generateQuarterWeeks = (quarterStartDate) => {
  const startDate = typeof quarterStartDate === 'string' ? parseISO(quarterStartDate) : quarterStartDate;
  const endDate = addWeeks(startDate, 12); // 3 mois = ~13 semaines
  
  return generateWeekRange(startDate, endDate);
};

// Validation des dates
export const isDateInRange = (date, startDate, endDate) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const startObj = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? parseISO(endDate) : endDate;
  
  return isWithinInterval(dateObj, { start: startObj, end: endObj });
};

// Calcul de différences
export const getWeeksBetween = (startDate, endDate) => {
  const start = getWeekStart(startDate);
  const end = getWeekStart(endDate);
  
  const weeks = [];
  let currentWeek = start;
  
  while (currentWeek <= end) {
    weeks.push(new Date(currentWeek));
    currentWeek = addWeeks(currentWeek, 1);
  }
  
  return weeks;
};

export const getProjectDuration = (startDate, endDate) => {
  const weeks = getWeeksBetween(startDate, endDate);
  return weeks.length;
};

// Utilitaires pour les trimestres
export const getQuarterStart = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  
  if (month < 3) return new Date(year, 0, 1); // Q1
  if (month < 6) return new Date(year, 3, 1); // Q2
  if (month < 9) return new Date(year, 6, 1); // Q3
  return new Date(year, 9, 1); // Q4
};

export const getQuarterEnd = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  
  if (month < 3) return new Date(year, 2, 31); // Q1
  if (month < 6) return new Date(year, 5, 30); // Q2
  if (month < 9) return new Date(year, 8, 30); // Q3
  return new Date(year, 11, 31); // Q4
};

export const getQuarterLabel = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  
  if (month < 3) return `Q1 ${year}`;
  if (month < 6) return `Q2 ${year}`;
  if (month < 9) return `Q3 ${year}`;
  return `Q4 ${year}`;
};

// Utilitaires pour les mois
export const getMonthLabel = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'MMMM yyyy', { locale: fr });
};

export const getMonthStart = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
};

export const getMonthEnd = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0);
};

// Parsing des dates
export const parseDate = (dateString, formatStr = 'dd/MM/yyyy') => {
  if (!dateString) return null;
  try {
    return parse(dateString, formatStr, new Date());
  } catch (error) {
    console.error('Erreur lors du parsing de la date:', error);
    return null;
  }
};

// Validation des semaines
export const isValidWeek = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const weekStart = getWeekStart(dateObj);
  return formatDate(dateObj) === formatDate(weekStart);
};

// Conversion des semaines pour l'API
export const weekToAPIFormat = (date) => {
  const weekStart = getWeekStart(date);
  return formatDateForAPI(weekStart);
};

export const weekFromAPIFormat = (dateString) => {
  return parseISO(dateString);
};

// Génération de dates par défaut
export const getDefaultDateRange = () => {
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const endOfYear = new Date(today.getFullYear(), 11, 31);
  
  return {
    start: startOfYear,
    end: endOfYear,
  };
};

export const getDefaultQuarter = () => {
  const today = new Date();
  return {
    start: getQuarterStart(today),
    end: getQuarterEnd(today),
  };
};

// Formatage pour les composants de sélection
export const formatWeekForSelect = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const weekStart = getWeekStart(dateObj);
  const weekEnd = getWeekEnd(dateObj);
  
  return {
    value: formatDateForAPI(weekStart),
    label: `Semaine du ${format(weekStart, 'dd MMM', { locale: fr })} au ${format(weekEnd, 'dd MMM yyyy', { locale: fr })}`,
  };
};

// Conversion du format HTML week input (ex: "2025-W27") vers date du lundi
export const weekInputToDate = (weekString) => {
  if (!weekString || !weekString.includes('W')) return null;
  
  const [year, week] = weekString.split('-W');
  const yearNum = parseInt(year, 10);
  const weekNum = parseInt(week, 10);
  
  // Utiliser le 4 janvier car c'est toujours dans la première semaine ISO
  const jan4 = new Date(yearNum, 0, 4);
  const firstWeekMonday = getWeekStart(jan4);
  
  // Ajouter le nombre de semaines nécessaires
  const targetWeek = addWeeks(firstWeekMonday, weekNum - 1);
  
  return targetWeek;
};

// Conversion d'une date vers le format HTML week input
export const dateToWeekInput = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const year = dateObj.getFullYear();
  const isoWeek = getISOWeek(dateObj);
  
  return `${year}-W${isoWeek.toString().padStart(2, '0')}`;
};

// Utilitaires pour les graphiques
export const getChartLabels = (weeks) => {
  return weeks.map(week => {
    const weekStart = getWeekStart(week);
    return format(weekStart, 'dd/MM', { locale: fr });
  });
};

export const getChartWeekLabels = (weeks) => {
  return weeks.map(week => {
    const weekStart = getWeekStart(week);
    return format(weekStart, 'dd MMM', { locale: fr });
  });
};

export default {
  formatDate,
  formatDateForInput,
  formatDateForAPI,
  getWeekStart,
  getWeekEnd,
  getWeekLabel,
  getCurrentWeek,
  getNextWeek,
  getPreviousWeek,
  generateWeekRange,
  generateQuarterWeeks,
  isDateInRange,
  getWeeksBetween,
  getProjectDuration,
  getQuarterStart,
  getQuarterEnd,
  getQuarterLabel,
  getMonthLabel,
  getMonthStart,
  getMonthEnd,
  parseDate,
  isValidWeek,
  weekToAPIFormat,
  weekFromAPIFormat,
  weekInputToDate,
  dateToWeekInput,
  getDefaultDateRange,
  getDefaultQuarter,
  formatWeekForSelect,
  getChartLabels,
  getChartWeekLabels,
}; 